
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const leaveTypes = [
  { type: 'Sick', desc: 'Medical appointments', accent: 'border-rose-400 dark:border-rose-400' },
  { type: 'Casual', desc: 'Personal time off', accent: 'border-amber-400 dark:border-amber-400' },
  { type: 'Earned', desc: 'Accumulated leave days', accent: 'border-emerald-400 dark:border-emerald-400' },
];

function Spinner() {
  return <Loader2 className="animate-spin h-5 w-5 mr-2 text-white" />;
}

export default function NewLeave() {
  const navigate = useNavigate();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().slice(0, 10);
  const maxDate = new Date(tomorrow.getFullYear(), tomorrow.getMonth() + 6, tomorrow.getDate()).toISOString().slice(0, 10);

  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(minDate);
  const [reason, setReason] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setCharCount(reason.length);
  }, [reason]);

  const getDays = () => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate);
    const e = new Date(endDate);
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  const validate = () => {
    const errs = {};
    if (!leaveType) errs.leaveType = 'Please select a leave type.';
    if (!startDate) errs.startDate = 'Start date is required.';
    if (!endDate) errs.endDate = 'End date is required.';
    if (startDate && endDate && endDate < startDate) errs.endDate = 'End date cannot be before start date.';
    if (!reason || reason.length < 10) errs.reason = 'Reason must be at least 10 characters.';
    if (reason.length > 500) errs.reason = 'Reason cannot exceed 500 characters.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setIsSubmitting(true);
    try {
      // Simulate API
      await new Promise(res => setTimeout(res, 1200));
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        navigate('/leaves');
      }, 1500);
    } catch {
      setErrors({ submit: 'Submission failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 dark:bg-gradient-to-br dark:from-gray-900 dark:to-indigo-900/30 flex items-center justify-center py-8 px-2 transition-colors duration-300">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl bg-white/90 dark:bg-gray-800/80 backdrop-blur-sm dark:backdrop-blur-lg p-6 sm:p-10 animate-fade-in-up"
      >
        {/* Header */}
        <div className="rounded-xl mb-8 px-6 py-5 bg-gradient-to-r from-indigo-600 to-purple-700 dark:from-indigo-800 dark:to-purple-900">
          <h2 className="text-2xl font-bold text-white">Request Leave</h2>
          <p className="text-indigo-100 dark:text-indigo-200 mt-1">Fill out the form to submit a new leave request</p>
        </div>

        {/* Leave Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Leave Type</label>
          <div className="space-y-3">
            {leaveTypes.map(({ type, desc, accent }) => (
              <label key={type} className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all duration-200 ${leaveType === type ? `border-indigo-500 bg-indigo-50 shadow-inner dark:border-indigo-400 dark:bg-indigo-900/30` : `border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50`} ${accent}`}>
                <input
                  type="radio"
                  name="leaveType"
                  value={type}
                  checked={leaveType === type}
                  onChange={() => setLeaveType(type)}
                  className="form-radio accent-indigo-500 dark:accent-indigo-400"
                />
                <div className="ml-3">
                  <span className="font-medium text-gray-800 dark:text-gray-100">{type} Leave</span>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{desc}</div>
                </div>
              </label>
            ))}
          </div>
          {errors.leaveType && <div className="text-rose-500 text-xs mt-2">{errors.leaveType}</div>}
        </div>

        {/* Date Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Start Date</label>
            <div className="relative">
              <Calendar className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  if (e.target.value > endDate) setEndDate(e.target.value);
                }}
                className="w-full pl-11 pr-3.5 py-3.5 rounded-lg border border-gray-300 dark:bg-gray-700/50 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                autoFocus
              />
            </div>
            {errors.startDate && <div className="text-rose-500 text-xs mt-2">{errors.startDate}</div>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">End Date</label>
            <div className="relative">
              <Calendar className="absolute left-3.5 top-3.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                min={startDate}
                max={maxDate}
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full pl-11 pr-3.5 py-3.5 rounded-lg border border-gray-300 dark:bg-gray-700/50 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
            {errors.endDate && <div className="text-rose-500 text-xs mt-2">{errors.endDate}</div>}
          </div>
        </div>

        {/* Duration Calculator */}
        <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 mb-6">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
            Requesting <span className="font-bold">{getDays()} days</span> of leave
            {getDays() > 5 && (
              <span className="ml-2 px-2 py-1 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 rounded-full">Extended leave</span>
            )}
          </p>
        </div>

        {/* Reason Field */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Reason</label>
          <div className="relative">
            <textarea
              rows={4}
              placeholder="Briefly explain your reason for leave..."
              className="w-full p-3.5 rounded-lg border border-gray-300 dark:bg-gray-700/50 dark:border-gray-600 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={510}
            />
            <div className={`absolute bottom-3 right-3 text-xs ${charCount > 500 ? 'text-rose-500 dark:text-rose-400' : charCount > 450 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {charCount}/500
            </div>
          </div>
          {errors.reason && <div className="text-rose-500 text-xs mt-2">{errors.reason}</div>}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="px-6 py-3.5 bg-white border border-gray-300 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors"
            onClick={() => navigate('/leaves')}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-6 py-3.5 font-medium text-white rounded-lg flex items-center justify-center ${isSubmitting ? 'bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 dark:from-indigo-700 dark:to-purple-700 dark:hover:from-indigo-600 dark:hover:to-purple-600'} transition-all duration-300 transform hover:scale-[1.02]`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Submit Request
              </>
            )}
          </button>
        </div>
        {errors.submit && <div className="text-rose-500 text-xs mt-4">{errors.submit}</div>}
      </form>
      {/* Success Toast */}
      {success && (
        <div className="fixed top-6 right-6 bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 px-4 py-3 rounded-lg shadow-lg flex items-center animate-fade-in-up z-50">
          <CheckCircle className="h-5 w-5 mr-2" />
          Leave request submitted successfully!
        </div>
      )}
    </div>
  );
}
